import React, { useMemo, useState, useEffect } from "react";
import Success from "../../../../formHelper/success";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";

type Id = string;
const uid = () => Math.random().toString(36).slice(2);

/** --- Types --- */
type TravelMode = "Bike" | "Car" | "Auto" | "Train" | "Others" | "Bus" | "";

interface TravelLeg {
  id: Id;
  from: string;
  to: string;
  mode: TravelMode | "";
  km?: number | "";
  rate?: number | "";
  amount?: number | "";
  otherMode?: string;
}

interface NamedAmount {
  id: Id;
  name: string;
  amount: number | "";
}

interface HotelItem {
  id: Id;
  days: number | "";
  amount: number | "";
}

interface LaborItem {
  id: Id;
  persons: number | "";
  amount: number | "";
}

interface TravelFormState {
  id: any;
  expanses_company_detail_id: any;
  legs: TravelLeg[];
  accompanying: { id: Id; name: string }[];
  food: NamedAmount[];
  hotel: HotelItem[];
  purchaseEquipment: NamedAmount[];
  purchaseHardware: NamedAmount[];
  labor: LaborItem[];
  otherExpenses: NamedAmount[];
  purpose: string;
}

interface Props {
  loadCompanyId?: number | null;
  onNext?: () => void;
  onPrev?: () => void;
}

type FormError = {
  department_customers?: {
    [index: number]: { [field: string]: string | undefined };
  };
} & { [key: string]: string | undefined };

/** --- Defaults / Constants --- */
const DEFAULT_BIKE_RATE = 6;
const DEFAULT_CAR_RATE = 8;

/** ---------- Hook: detect mobile ---------- */
const useIsMobile = (maxWidth = 767.98) => {
  const isClient = typeof window !== "undefined";
  const [isMobile, setIsMobile] = useState<boolean>(
    isClient ? window.innerWidth <= maxWidth : false
  );
  useEffect(() => {
    if (!isClient) return;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsMobile("matches" in e ? e.matches : mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler as any);
    else (mq as any).addListener(handler as any);
    setIsMobile(mq.matches);
    return () => {
      if (mq.removeEventListener)
        mq.removeEventListener("change", handler as any);
      else (mq as any).removeListener(handler as any);
    };
  }, [maxWidth, isClient]);
  return isMobile;
};

/** robust usePrefersDark */
const usePrefersDark = (): boolean => {
  const get = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  const [isDark, setIsDark] = useState<boolean>(get());

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (event: MediaQueryListEvent | MediaQueryList) => {
      const matches =
        typeof (event as any).matches === "boolean"
          ? (event as any).matches
          : mq.matches;
      setIsDark(matches);
    };
    if (typeof mq.addEventListener === "function")
      mq.addEventListener("change", handler as EventListener);
    else (mq as any).addListener(handler);
    setIsDark(mq.matches);
    return () => {
      if (typeof mq.removeEventListener === "function")
        mq.removeEventListener("change", handler as EventListener);
      else (mq as any).removeListener(handler);
    };
  }, []);

  return isDark;
};

export default function TravelDetails({ onNext, onPrev }: Props): JSX.Element {
  const isMobile = useIsMobile();
  const isDark = usePrefersDark();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [formError, setFormError] = useState<FormError>({});

  const backendRoute = all_routes.backend;

  const [form, setForm] = useState<TravelFormState>({
    id: "",
    expanses_company_detail_id: "",
    legs: [
      {
        id: uid(),
        from: "",
        to: "",
        mode: "",
        km: "",
        rate: "",
        amount: "",
        otherMode: "",
      },
    ],
    accompanying: [{ id: uid(), name: "" }],
    food: [{ id: uid(), name: "", amount: "" }],
    hotel: [{ id: uid(), days: "", amount: "" }],
    purchaseEquipment: [{ id: uid(), name: "", amount: "" }],
    purchaseHardware: [{ id: uid(), name: "", amount: "" }],
    labor: [{ id: uid(), persons: "", amount: "" }],
    otherExpenses: [{ id: uid(), name: "", amount: "" }],
    purpose: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  /* ---------- form helpers ---------- */
  const setLegField = (id: Id, field: keyof TravelLeg, value: any) =>
    setForm((p) => ({
      ...p,
      legs: p.legs.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
    }));

  const addLeg = () =>
    setForm((p) => ({
      ...p,
      legs: [
        ...p.legs,
        {
          id: uid(),
          from: "",
          to: "",
          mode: "",
          km: "",
          rate: "",
          amount: "",
          otherMode: "",
        },
      ],
    }));

  const removeLeg = (id: Id) =>
    setForm((p) => ({
      ...p,
      legs: p.legs.length > 1 ? p.legs.filter((l) => l.id !== id) : p.legs,
    }));

  const addRow = <T extends { id: Id }>(list: T[], empty: T) => [
    ...list,
    empty,
  ];
  const removeRow = <T extends { id: Id }>(list: T[], id: Id) =>
    list.length > 1 ? list.filter((x) => x.id !== id) : list;

  const setList =
    <K extends keyof TravelFormState>(key: K) =>
    (updater: (list: any[]) => any[]) =>
      setForm((p) => ({ ...p, [key]: updater(p[key] as any[]) }));

  /* ---------- mode & amount logic ---------- */
  const onModeChange = (id: Id, mode: TravelMode | "") =>
    setForm((p: any) => {
      const legs = p.legs.map((leg: any) => {
        if (leg.id !== id) return leg;
        if (mode === "Bike") {
          const km = Number(leg.km || 0);
          const rate = Number(leg.rate || DEFAULT_BIKE_RATE);
          return {
            ...leg,
            mode,
            otherMode: "",
            rate,
            amount: +(km * rate).toFixed(2),
          };
        }
        if (mode === "Car") {
          const km = Number(leg.km || 0);
          const rate = Number(leg.rate || DEFAULT_CAR_RATE);
          return {
            ...leg,
            mode,
            otherMode: "",
            rate,
            amount: +(km * rate).toFixed(2),
          };
        }
        return {
          ...leg,
          mode,
          rate: "",
          amount: leg.amount || "",
          otherMode: leg.otherMode || "",
        };
      });
      return { ...p, legs };
    });

  const onKmOrRateChange = (id: Id, field: "km" | "rate", raw: string) => {
    const val = raw === "" ? "" : Number(raw);
    setForm((p) => {
      const legs = p.legs.map((leg) => {
        if (leg.id !== id) return leg;
        const next: TravelLeg = { ...leg, [field]: val };
        if (leg.mode === "Bike" || leg.mode === "Car") {
          const km = Number(field === "km" ? val : next.km || 0);
          const rate = Number(
            field === "rate"
              ? val
              : next.rate ||
                  (leg.mode === "Bike" ? DEFAULT_BIKE_RATE : DEFAULT_CAR_RATE)
          );
          next.amount = km === 0 || isNaN(km) ? "" : +(km * rate).toFixed(2);
        }
        return next;
      });
      return { ...p, legs };
    });
  };

  /* ---------- totals ---------- */
  const totals = useMemo(() => {
    const sum = (arr: { amount?: number | "" }[]) =>
      arr.reduce((acc, x) => acc + (Number(x.amount) || 0), 0);
    const travel = form.legs.reduce(
      (acc, l) => acc + (Number(l.amount) || 0),
      0
    );
    const food = sum(form.food);
    const hotel = form.hotel.reduce(
      (acc, h) => acc + (Number(h.amount) || 0),
      0
    );
    const equip = sum(form.purchaseEquipment);
    const hardware = sum(form.purchaseHardware);
    const labor = form.labor.reduce(
      (acc, l) => acc + (Number(l.amount) || 0),
      0
    );
    const others = sum(form.otherExpenses);
    const grand = travel + food + hotel + equip + hardware + labor + others;
    return { travel, food, hotel, equip, hardware, labor, others, grand };
  }, [form]);

  /* ---------- build payload ---------- */
  const buildPayload = () => {
    const payload = {
      id: form.id ?? null,
      expanses_company_detail_id: form.expanses_company_detail_id ?? null,
      legs: form.legs.map((l) => ({
        from: l.from || null,
        to: l.to || null,
        mode: l.mode || null,
        km: l.km === "" ? null : Number(l.km ?? 0),
        rate: l.rate === "" ? null : Number(l.rate ?? 0),
        amount: l.amount === "" ? null : Number(l.amount ?? 0),
        otherMode: l.otherMode || null,
      })),
      accompanying: form.accompanying.map((a) => ({ name: a.name || null })),
      food: form.food.map((f) => ({
        name: f.name || null,
        amount: f.amount === "" ? null : Number(f.amount),
      })),
      hotel: form.hotel.map((h) => ({
        days: h.days === "" ? null : Number(h.days),
        amount: h.amount === "" ? null : Number(h.amount),
      })),
      purchaseEquipment: form.purchaseEquipment.map((p) => ({
        name: p.name || null,
        amount: p.amount === "" ? null : Number(p.amount),
      })),
      purchaseHardware: form.purchaseHardware.map((p) => ({
        name: p.name || null,
        amount: p.amount === "" ? null : Number(p.amount),
      })),
      labor: form.labor.map((l) => ({
        persons: l.persons === "" ? null : Number(l.persons),
        amount: l.amount === "" ? null : Number(l.amount),
      })),
      otherExpenses: form.otherExpenses.map((o) => ({
        name: o.name || null,
        amount: o.amount === "" ? null : Number(o.amount),
      })),
      purpose: form.purpose || null,
      totals: totals,
    } as const;
    return payload;
  };

  /* ---------- populate form from server payload ---------- */
  const populateFromPayload = (payload: any) => {
    if (!payload) return;
    const p = payload?.data?.travel_expanses ?? payload;
    setForm({
      id: p.id ?? null,
      expanses_company_detail_id: payload?.data?.id  ?? null,
      legs:
        Array.isArray(p.legs) && p.legs.length
          ? p.legs.map((l: any) => ({
              id: uid(),
              from: l.from ?? "",
              to: l.to ?? "",
              mode: l.mode ?? "",
              km: l.km == null ? "" : Number(l.km),
              rate: l.rate == null ? "" : Number(l.rate),
              amount: l.amount == null ? "" : Number(l.amount),
              otherMode: l.otherMode ?? "",
            }))
          : [
              {
                id: uid(),
                from: "",
                to: "",
                mode: "",
                km: "",
                rate: "",
                amount: "",
                otherMode: "",
              },
            ],
      accompanying:
        Array.isArray(p.accompanying) && p.accompanying.length
          ? p.accompanying.map((a: any) => ({ id: uid(), name: a.name ?? "" }))
          : [{ id: uid(), name: "" }],
      food:
        Array.isArray(p.food) && p.food.length
          ? p.food.map((f: any) => ({
              id: uid(),
              name: f.name ?? "",
              amount: f.amount == null ? "" : Number(f.amount),
            }))
          : [{ id: uid(), name: "", amount: "" }],
      hotel:
        Array.isArray(p.hotel) && p.hotel.length
          ? p.hotel.map((h: any) => ({
              id: uid(),
              days: h.days == null ? "" : Number(h.days),
              amount: h.amount == null ? "" : Number(h.amount),
            }))
          : [{ id: uid(), days: "", amount: "" }],
      purchaseEquipment:
        Array.isArray(p.purchaseEquipment) && p.purchaseEquipment.length
          ? p.purchaseEquipment.map((x: any) => ({
              id: uid(),
              name: x.name ?? "",
              amount: x.amount == null ? "" : Number(x.amount),
            }))
          : [{ id: uid(), name: "", amount: "" }],
      purchaseHardware:
        Array.isArray(p.purchaseHardware) && p.purchaseHardware.length
          ? p.purchaseHardware.map((x: any) => ({
              id: uid(),
              name: x.name ?? "",
              amount: x.amount == null ? "" : Number(x.amount),
            }))
          : [{ id: uid(), name: "", amount: "" }],
      labor:
        Array.isArray(p.labor) && p.labor.length
          ? p.labor.map((x: any) => ({
              id: uid(),
              persons: x.persons == null ? "" : Number(x.persons),
              amount: x.amount == null ? "" : Number(x.amount),
            }))
          : [{ id: uid(), persons: "", amount: "" }],
      otherExpenses:
        Array.isArray(p.other_expenses) && p.other_expenses.length
          ? p.other_expenses.map((x: any) => ({
              id: uid(),
              name: x.name ?? "",
              amount: x.amount == null ? "" : Number(x.amount),
            }))
          : [{ id: uid(), name: "", amount: "" }],
      purpose: p.purpose ?? "",
    });
  };

  const capitalize = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

  const parseErrors = (errors: Record<string, string>): FormError => {
    const result: FormError = {};
    for (const key in errors) {
      if (key.startsWith("department_customers")) {
        const parts = key.split(".");
        const index = Number(parts[1]);
        const field = parts[2];
        if (!isNaN(index) && field) {
          result.department_customers ??= {};
          result.department_customers[index] ??= {};
          const readableField = field.replace(/_/g, " ");
          result.department_customers[index][field] = `${capitalize(
            readableField
          )} field is required.`;
        }
      } else {
        result[key] = errors[key];
      }
    }
    return result;
  };

  /* ---------- submit ---------- */
  const submit = async () => {
    const payload = buildPayload();

    setLoading(true);
    setErrors({});

    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.addUpdateTravelExpense,
        {
          data: payload,
        }
      );
      if (res?.data?.code === 221) {
        if (typeof (window as any).setFormError === "function") {
          (window as any).setFormError(parseErrors(res?.data?.errors || {}));
        } else if (typeof setFormError !== "undefined") {
          setFormError(parseErrors(res?.data?.errors || {}));
        }
        return;
      }

      if (res?.data) {
        populateFromPayload(res.data);
        tryLoadCmp();
        if (typeof setShowSuccess !== "undefined") {
          setShowSuccess(true);
        }
        if (typeof setShowMessage !== "undefined") {
          setShowMessage(res?.data?.message ?? "Saved successfully");
        }
      }
    } catch (err: any) {
      console.error("Save failed", err);
      alert("Save failed (see console)");
      setErrors({ server: err?.message ?? "Network error" });
    } finally {
      setLoading(false);
    }
  };

  const tryLoadCmp = async () => {
    try {
      const res = await apiRequestCall("post", backendRoute.getExpansesDetails);
      if (res?.data) populateFromPayload(res.data);
    } catch (err) {
      console.error("Failed to load company details:", err);
    }
  };

  useEffect(() => {
    tryLoadCmp();
  }, []);

  /* ---------- styles & render helpers (unchanged from your original) ---------- */
  const mobileCardStyle: React.CSSProperties = {
    border: isDark
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(0,0,0,0.06)",
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  };

  const actionButtonStyle1: React.CSSProperties = {
    width: 36,
    height: 36,
    padding: 0,
    minWidth: 36,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
  };

  const accompanyingRows = (
    list: { id: string; name: string }[],
    key: keyof TravelFormState
  ) =>
    list.map((p, i) =>
      isMobile ? (
        <div
          key={p.id}
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <input
            className="form-control"
            placeholder="Colleague name"
            value={p.name}
            onChange={(e) =>
              setList(key as any)((l: any[]) =>
                l.map((x: any) =>
                  x.id === p.id ? { ...x, name: e.target.value } : x
                )
              )
            }
            style={{ flex: 1, minWidth: 0 }}
          />

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              type="button"
              className="btn btn-icon btn-sm btn-soft-danger"
              onClick={() =>
                setList(key as any)((l: any[]) => removeRow(l, p.id))
              }
              disabled={list.length === 1}
              aria-label="remove"
              style={actionButtonStyle1}
            >
              <i className="feather-trash" />
            </button>

            {i === list.length - 1 && (
              <button
                type="button"
                className="btn btn-icon btn-sm btn-soft-info"
                onClick={() =>
                  setList(key as any)((l: any[]) =>
                    addRow(l, { id: uid(), name: "" } as any)
                  )
                }
                aria-label="add"
                style={actionButtonStyle1}
              >
                <i className="feather-plus" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div key={p.id} className="row g-2 align-items-center mb-2">
          <div className="col-10 col-sm-10" style={{ minWidth: 0 }}>
            <input
              className="form-control"
              placeholder="Colleague name"
              value={p.name}
              onChange={(e) =>
                setList(key as any)((l: any[]) =>
                  l.map((x: any) =>
                    x.id === p.id ? { ...x, name: e.target.value } : x
                  )
                )
              }
            />
          </div>
          <div
            className="col-2 d-flex gap-2"
            style={{ justifyContent: "flex-end" }}
          >
            <button
              type="button"
              className="btn btn-icon btn-sm btn-soft-danger"
              onClick={() =>
                setList(key as any)((l: any[]) => removeRow(l, p.id))
              }
              disabled={list.length === 1}
              aria-label="remove"
            >
              <i className="feather-trash" />
            </button>
            {i === list.length - 1 && (
              <button
                type="button"
                className="btn btn-icon btn-sm btn-soft-info"
                onClick={() =>
                  setList(key as any)((l: any[]) =>
                    addRow(l, { id: uid(), name: "" } as any)
                  )
                }
                aria-label="add"
              >
                <i className="feather-plus" />
              </button>
            )}
          </div>
        </div>
      )
    );

  const purchaseRows = (list: NamedAmount[], key: keyof TravelFormState) =>
    list.map((r, i) =>
      isMobile ? (
        <div
          key={r.id}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <input
            className="form-control"
            placeholder="Description"
            value={r.name}
            onChange={(e) =>
              setList(key as any)((l) =>
                l.map((x: any) =>
                  x.id === r.id ? { ...x, name: e.target.value } : x
                )
              )
            }
            style={{ minWidth: 0 }}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="number"
              className="form-control"
              placeholder="Amount"
              min={0}
              value={r.amount ?? ""}
              onChange={(e) =>
                setList(key as any)((l) =>
                  l.map((x: any) =>
                    x.id === r.id
                      ? {
                          ...x,
                          amount:
                            e.target.value === "" ? "" : Number(e.target.value),
                        }
                      : x
                  )
                )
              }
              style={{ width: "100%", minWidth: 0 }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn btn-icon btn-sm btn-soft-danger"
                onClick={() => setList(key as any)((l) => removeRow(l, r.id))}
                disabled={list.length === 1}
                aria-label="remove"
              >
                <i className="feather-trash" />
              </button>
              {i === list.length - 1 && (
                <button
                  type="button"
                  className="btn btn-icon btn-sm btn-soft-info"
                  onClick={() =>
                    setList(key as any)((l) =>
                      addRow(l, { id: uid(), name: "", amount: "" } as any)
                    )
                  }
                  aria-label="add"
                >
                  <i className="feather-plus" />
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div key={r.id} className="row g-2 align-items-center mb-2">
          <div className="col-7 col-sm-8" style={{ minWidth: 0 }}>
            <input
              className="form-control"
              placeholder="Description"
              value={r.name}
              onChange={(e) =>
                setList(key as any)((l) =>
                  l.map((x: any) =>
                    x.id === r.id ? { ...x, name: e.target.value } : x
                  )
                )
              }
            />
          </div>
          <div className="col-3 col-sm-2" style={{ minWidth: 0 }}>
            <input
              type="number"
              className="form-control"
              placeholder="Amount"
              min={0}
              value={r.amount ?? ""}
              onChange={(e) =>
                setList(key as any)((l) =>
                  l.map((x: any) =>
                    x.id === r.id
                      ? {
                          ...x,
                          amount:
                            e.target.value === "" ? "" : Number(e.target.value),
                        }
                      : x
                  )
                )
              }
            />
          </div>
          <div
            className="col-2 d-flex gap-2"
            style={{ justifyContent: "flex-end" }}
          >
            <button
              type="button"
              className="btn btn-icon btn-sm btn-soft-danger"
              onClick={() => setList(key as any)((l) => removeRow(l, r.id))}
              disabled={list.length === 1}
              aria-label="remove"
            >
              <i className="feather-trash" />
            </button>
            {i === list.length - 1 && (
              <button
                type="button"
                className="btn btn-icon btn-sm btn-soft-info"
                onClick={() =>
                  setList(key as any)((l) =>
                    addRow(l, { id: uid(), name: "", amount: "" } as any)
                  )
                }
                aria-label="add"
              >
                <i className="feather-plus" />
              </button>
            )}
          </div>
        </div>
      )
    );

  const hotelRows = (list: HotelItem[]) =>
    list.map((h, i) =>
      isMobile ? (
        <div
          key={h.id}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <input
            type="number"
            className="form-control"
            placeholder="No. of days"
            min={0}
            value={h.days ?? ""}
            onChange={(e) =>
              setList("hotel")((arr) =>
                arr.map((x) =>
                  x.id === h.id
                    ? {
                        ...x,
                        days:
                          e.target.value === "" ? "" : Number(e.target.value),
                      }
                    : x
                )
              )
            }
            style={{ minWidth: 0 }}
          />

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="number"
              className="form-control"
              placeholder="Amount"
              min={0}
              value={h.amount ?? ""}
              onChange={(e) =>
                setList("hotel")((arr) =>
                  arr.map((x) =>
                    x.id === h.id
                      ? {
                          ...x,
                          amount:
                            e.target.value === "" ? "" : Number(e.target.value),
                        }
                      : x
                  )
                )
              }
              style={{ width: "100%", minWidth: 0 }}
            />

            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn btn-icon btn-sm btn-soft-danger"
                onClick={() => setList("hotel")((arr) => removeRow(arr, h.id))}
                disabled={list.length === 1}
              >
                <i className="feather-trash" />
              </button>

              {i === list.length - 1 && (
                <button
                  type="button"
                  className="btn btn-icon btn-sm btn-soft-info"
                  onClick={() =>
                    setList("hotel")((arr) =>
                      addRow(arr, { id: uid(), days: "", amount: "" } as any)
                    )
                  }
                >
                  <i className="feather-plus" />
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div key={h.id} className="row g-2 align-items-center mb-2">
          <div className="col-3" style={{ minWidth: 0 }}>
            <input
              type="number"
              className="form-control"
              placeholder="No. of days"
              min={0}
              value={h.days ?? ""}
              onChange={(e) =>
                setList("hotel")((arr) =>
                  arr.map((x) =>
                    x.id === h.id
                      ? {
                          ...x,
                          days:
                            e.target.value === "" ? "" : Number(e.target.value),
                        }
                      : x
                  )
                )
              }
            />
          </div>

          <div className="col-7" style={{ minWidth: 0 }}>
            <input
              type="number"
              className="form-control"
              placeholder="Amount"
              min={0}
              value={h.amount ?? ""}
              onChange={(e) =>
                setList("hotel")((arr) =>
                  arr.map((x) =>
                    x.id === h.id
                      ? {
                          ...x,
                          amount:
                            e.target.value === "" ? "" : Number(e.target.value),
                        }
                      : x
                  )
                )
              }
            />
          </div>

          <div
            className="col-2 d-flex gap-2"
            style={{ justifyContent: "flex-end" }}
          >
            <button
              type="button"
              className="btn btn-icon btn-sm btn-soft-danger"
              onClick={() => setList("hotel")((arr) => removeRow(arr, h.id))}
              disabled={list.length === 1}
            >
              <i className="feather-trash" />
            </button>
            {i === list.length - 1 && (
              <button
                type="button"
                className="btn btn-icon btn-sm btn-soft-info"
                onClick={() =>
                  setList("hotel")((arr) =>
                    addRow(arr, { id: uid(), days: "", amount: "" } as any)
                  )
                }
              >
                <i className="feather-plus" />
              </button>
            )}
          </div>
        </div>
      )
    );

  /* --- desktop rows for travel table --- */
  const desktopRows = form.legs.map((leg, idx) => (
    <tr key={leg.id}>
      <td>
        <input
          className="form-control"
          value={leg.from}
          onChange={(e) => setLegField(leg.id, "from", e.target.value)}
          placeholder="From"
        />
        {errors[`leg_${idx}_from`] && (
          <div className="text-danger small">{errors[`leg_${idx}_from`]}</div>
        )}
      </td>

      <td>
        <input
          className="form-control"
          value={leg.to}
          onChange={(e) => setLegField(leg.id, "to", e.target.value)}
          placeholder="To"
        />
        {errors[`leg_${idx}_to`] && (
          <div className="text-danger small">{errors[`leg_${idx}_to`]}</div>
        )}
      </td>

      <td>
        <select
          className="form-select"
          value={leg.mode}
          onChange={(e) => onModeChange(leg.id, e.target.value as TravelMode)}
        >
          <option value="">Select</option>
          <option value="Bike">Bike</option>
          <option value="Car">Car</option>
          <option value="Train">Train</option>
          <option value="Auto">Auto</option>
          <option value="Bus">Bus</option>
          <option value="Others">Others</option>
        </select>
        {errors[`leg_${idx}_mode`] && (
          <div className="text-danger small">{errors[`leg_${idx}_mode`]}</div>
        )}
        {leg.mode === "Others" && (
          <>
            <input
              className="form-control mt-2"
              value={leg.otherMode || ""}
              onChange={(e) => setLegField(leg.id, "otherMode", e.target.value)}
              placeholder="Enter travel mode"
            />
            {errors[`leg_${idx}_otherMode`] && (
              <div className="text-danger small">
                {errors[`leg_${idx}_otherMode`]}
              </div>
            )}
          </>
        )}
      </td>

      <td>
        <input
          type="number"
          className="form-control"
          value={leg.mode === "Others" ? "" : leg.km ?? ""}
          onChange={(e) => onKmOrRateChange(leg.id, "km", e.target.value)}
          placeholder="KM"
          disabled={leg.mode === "Others"}
          min={0}
        />
        {errors[`leg_${idx}_km`] && (
          <div className="text-danger small">{errors[`leg_${idx}_km`]}</div>
        )}
      </td>

      <td>
        <input
          type="number"
          className="form-control"
          value={leg.rate ?? ""}
          onChange={(e) => onKmOrRateChange(leg.id, "rate", e.target.value)}
          placeholder="Rate"
          disabled={leg.mode === "Others"}
          min={0}
        />
        {errors[`leg_${idx}_rate`] && (
          <div className="text-danger small">{errors[`leg_${idx}_rate`]}</div>
        )}
      </td>

      <td>
        <input
          type="number"
          className="form-control"
          value={leg.amount ?? ""}
          onChange={(e) =>
            setLegField(
              leg.id,
              "amount",
              e.target.value === "" ? "" : Number(e.target.value)
            )
          }
          placeholder="Amount"
          min={0}
        />
        {errors[`leg_${idx}_amount`] && (
          <div className="text-danger small">{errors[`leg_${idx}_amount`]}</div>
        )}
      </td>

      <td style={{ textAlign: isMobile ? "right" : "center" }}>
        <div style={{ display: "inline-flex", gap: 8, alignItems: "end" }}>
          <button
            type="button"
            className="btn btn-icon btn-sm btn-soft-danger"
            onClick={() => removeLeg(leg.id)}
            style={{
              opacity: form.legs.length === 1 ? 0.5 : 1,
            }}
            title={
              form.legs.length === 1
                ? "At least one row required"
                : "Remove row"
            }
          >
            <i className="feather-trash" />
          </button>
          {idx === form.legs.length - 1 && (
            <button
              type="button"
              className="btn btn-icon btn-sm btn-soft-info"
              onClick={addLeg}
              title="Add row"
            >
              <i className="feather-plus" />
            </button>
          )}
        </div>
      </td>
    </tr>
  ));

  const mobileCards = form.legs.map((leg, idx) => (
    <div key={leg.id} style={mobileCardStyle}>
      <div style={{ marginBottom: 8 }}>
        <label style={{ display: "block", width: "100%" }}>
          <small style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            From *
          </small>
          <input
            style={{ width: "100%" }}
            className="form-control"
            value={leg.from}
            onChange={(e) => setLegField(leg.id, "from", e.target.value)}
            placeholder="From"
          />
          {errors[`leg_${idx}_from`] && (
            <div className="text-danger small">{errors[`leg_${idx}_from`]}</div>
          )}
        </label>
      </div>

      <div style={{ marginBottom: 8 }}>
        <label style={{ display: "block", width: "100%" }}>
          <small style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            To *
          </small>
          <input
            style={{ width: "100%" }}
            className="form-control"
            value={leg.to}
            onChange={(e) => setLegField(leg.id, "to", e.target.value)}
            placeholder="To"
          />
          {errors[`leg_${idx}_to`] && (
            <div className="text-danger small">{errors[`leg_${idx}_to`]}</div>
          )}
        </label>
      </div>

      <div style={{ marginBottom: 8 }}>
        <label style={{ display: "block", width: "100%" }}>
          <small style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Mode *
          </small>
          <select
            style={{ width: "100%" }}
            className="form-select"
            value={leg.mode}
            onChange={(e) => onModeChange(leg.id, e.target.value as TravelMode)}
          >
            <option value="">Select</option>
            <option value="Bike">Bike</option>
            <option value="Car">Car</option>
            <option value="Train">Train</option>
            <option value="Auto">Auto</option>
            <option value="Bus">Bus</option>
            <option value="Others">Others</option>
          </select>
          {errors[`leg_${idx}_mode`] && (
            <div className="text-danger small">{errors[`leg_${idx}_mode`]}</div>
          )}
        </label>

        {leg.mode === "Others" && (
          <>
            <input
              className="form-control"
              style={{ marginTop: 8, width: "100%" }}
              value={leg.otherMode || ""}
              onChange={(e) => setLegField(leg.id, "otherMode", e.target.value)}
              placeholder="Enter travel mode"
            />
            {errors[`leg_${idx}_otherMode`] && (
              <div className="text-danger small">
                {errors[`leg_${idx}_otherMode`]}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={{ display: "block" }}>
            <small
              style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
            >
              KM
            </small>
            <input
              type="number"
              className="form-control"
              value={leg.km ?? ""}
              onChange={(e) => onKmOrRateChange(leg.id, "km", e.target.value)}
              placeholder="KM"
              disabled={leg.mode === "Others"}
              min={0}
            />
            {errors[`leg_${idx}_km`] && (
              <div className="text-danger small">{errors[`leg_${idx}_km`]}</div>
            )}
          </label>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={{ display: "block" }}>
            <small
              style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
            >
              Rate
            </small>
            <input
              type="number"
              className="form-control"
              value={leg.rate ?? ""}
              onChange={(e) => onKmOrRateChange(leg.id, "rate", e.target.value)}
              placeholder="Rate"
              disabled={leg.mode === "Others"}
              min={0}
            />
            {errors[`leg_${idx}_rate`] && (
              <div className="text-danger small">
                {errors[`leg_${idx}_rate`]}
              </div>
            )}
          </label>
        </div>
      </div>

      <div
        style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <label style={{ display: "block" }}>
            <small
              style={{ display: "block", marginBottom: 6, fontWeight: 600 }}
            >
              Amount
            </small>
            <input
              type="number"
              className="form-control"
              value={leg.amount ?? ""}
              onChange={(e) =>
                setLegField(
                  leg.id,
                  "amount",
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
              placeholder="Amount"
              min={0}
            />
            {errors[`leg_${idx}_amount`] && (
              <div className="text-danger small">
                {errors[`leg_${idx}_amount`]}
              </div>
            )}
          </label>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="btn btn-sm btn-soft-danger"
            onClick={() => removeLeg(leg.id)}
            style={{
              opacity: form.legs.length === 1 ? 0.5 : 1,
            }}
            disabled={form.legs.length === 1}
          >
            <i className="feather-trash" />
          </button>

          {idx === form.legs.length - 1 && (
            <button
              type="button"
              className="btn btn-sm btn-soft-info"
              onClick={addLeg}
            >
              <i className="feather-plus" />
            </button>
          )}
        </div>
      </div>
    </div>
  ));

  return (
    <div className="supplier-table-root" style={{ padding: isMobile ? 12 : 0 }}>
      {/* Travel header */}
      <div className="mt-3" style={{ marginBottom: 8 }}>
        <small>
          <label style={{ display: "block", fontWeight: 600 }}>
            Travel location to &amp; from{" "}
            <span style={{ color: "#d9534f" }}>*</span>
          </label>
        </small>
      </div>

      {/* Desktop: table, Mobile: cards */}
      {!isMobile ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "var(--thead-bg, #f8f9fa)" }}>
              <tr>
                <th style={{ minWidth: 150, textAlign: "left", padding: 10 }}>
                  From *
                </th>
                <th style={{ minWidth: 150, textAlign: "left", padding: 8 }}>
                  To *
                </th>
                <th style={{ minWidth: 150, textAlign: "left", padding: 8 }}>
                  Mode *
                </th>
                <th style={{ minWidth: 150, textAlign: "left", padding: 8 }}>
                  KM
                </th>
                <th style={{ minWidth: 150, textAlign: "left", padding: 8 }}>
                  Rate
                </th>
                <th style={{ minWidth: 150, textAlign: "left", padding: 8 }}>
                  Amount *
                </th>
                <th style={{ minWidth: 110, textAlign: "center", padding: 8 }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>{desktopRows}</tbody>
          </table>
          {errors["legs"] && (
            <div style={{ color: "#d9534f", fontSize: 12 }}>
              {errors["legs"]}
            </div>
          )}
        </div>
      ) : (
        <div>{mobileCards}</div>
      )}

      {/* The rest (accompanying, food, hotel, labor, other) */}
      <div style={mobileCardStyle}>
        <div style={{ marginBottom: 8 }} className="col-7 col-sm-12">
          <small>
            <label style={{ display: "block", fontWeight: 600 }}>
              Accompanying Person's
            </label>
          </small>
          {accompanyingRows(form.accompanying, "accompanying")}
        </div>

        {/* Food */}
        <div style={{ marginTop: 8 }}>
          <small>
            <label style={{ display: "block", fontWeight: 600 }}>
              Food Details
            </label>
          </small>
          {purchaseRows(form.food, "food")}
        </div>

        {/* Hotel */}
        <div style={{ marginTop: 8 }}>
          <small>
            <label style={{ display: "block", fontWeight: 600 }}>
              Hotel – No. of days & Amount
            </label>
          </small>
          {hotelRows(form.hotel)}
        </div>

        {/* Other Expenses */}
        <div style={{ marginTop: 8 }}>
          <small>
            <label style={{ display: "block", fontWeight: 600 }}>
              Other Expenses
            </label>
          </small>
          {purchaseRows(form.otherExpenses, "otherExpenses")}
        </div>

        {/* Totals & Actions */}
        <div
          style={{
            display: "flex",
            gap: 12,
            marginTop: 14,
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          <div style={{ flex: 1, color: "red", fontSize: 13 }}>
            {/* server error */}
            {errors.server && (
              <div className="text-danger">{errors.server}</div>
            )}
          </div>

          <div style={{ width: isMobile ? "100%" : 320 }}>
            <div
              style={{
                border: "1px solid var(--rt-border-color, rgba(0,0,0,0.06))",
                borderRadius: 6,
              }}
            >
              <div style={{ padding: "10px 12px" }}>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>Travel</span>
                  <strong>₹{totals.travel.toFixed(2)}</strong>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>Food</span>
                  <strong>₹{totals.food.toFixed(2)}</strong>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>Hotel</span>
                  <strong>₹{totals.hotel.toFixed(2)}</strong>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderTop:
                      "1px solid var(--rt-border-color, rgba(0,0,0,0.06))",
                    paddingTop: 8,
                  }}
                >
                  <span>Other Expenses</span>
                  <strong>₹{totals.others.toFixed(2)}</strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderTop:
                      "1px solid var(--rt-border-color, rgba(0,0,0,0.06))",
                    paddingTop: 10,
                  }}
                >
                  <h5>
                    <span>Total</span>
                  </h5>
                  <strong>₹{totals.grand.toFixed(2)}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 12,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <button
            type="button"
            className="btn btn-soft-warning"
            style={{ width: isMobile ? "100%" : undefined }}
            onClick={onPrev}
          >
            <i className="ti ti-arrow-left mb-1" /> back
          </button>

          <button
            type="button"
            className="btn btn-soft-success"
            onClick={submit}
            style={{ width: isMobile ? "100%" : undefined }}
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
                Saving...
              </>
            ) : (
              <>
                Save & Next   {" "} <i className="ti ti-arrow-right" />
              </>
            )}
          </button>
        </div>
      </div>
      <Success
        id="OwnerSuccessModal"
        title="Travel info"
        message={showMessage}
        show={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          try {
            onNext?.();
          } catch (err) {
            console.error("onNext callback failed", err);
          }
        }}
      />
    </div>
  );
}
